import React, { useState, useEffect, useContext } from 'react';

import Panel from '../../../Components/Panel/Panel';
import PanelListDefault from '../../../Components/PanelListDefault/PanelListDefault';
import RemoveIdsNotFound from '../../../Components/RemoveIdsNotFound/RemoveIdsNotFound';
import ExecuteAxios from '../../../Components/ExecuteAxios/ExecuteAxios';
import ResetVariables from '../../../Components/ResetVariables/ResetVariables';
import ApplyAttributes from '../../../Components/ApplyAttributes/ApplyAttributes';
import AutomaticallyListedAttributes from '../../../Components/AutomaticallyListedAttributes/AutomaticallyListedAttributes';
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator';
import { AuthContext } from '../../../Contexts/auth';
import { isVisible } from '@testing-library/user-event/dist/utils';

function Topics({
    className = '',
    style,
    titlePage = 'Tópicos',
    onSelectedsChange,
    statusRules
}) {
    const { clientSelected, clientSelectedHandled, setClientSelectedHandled } = useContext(AuthContext);
    const [id, setId] = useState(null);
    const [descriptionSubject, setDescriptionSubject] = useState(null);
    const [status, setStatus] = useState('A');
    const [isActive, setIsActive] = useState('S');

    const [topicsRecords, setTopicsRecords] = useState(null);
    const [topicsRecordDetail, setTopicsRecordDetail] = useState(null);
    const [selectedsTopics, setSelectedsTopics] = useState([]);
    const [inserting, setInserting] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [loadingPanel, setLoadingPanel] = useState(false);

    const attributeList = [
        { name: "id", value: id, setter: setId },
        { name: "descriptionSubject", value: descriptionSubject, setter: setDescriptionSubject },
        {
            name: "isActive", value: isActive, setter: setIsActive, defaultValue: 'S',
            isVisible: statusRules !== 'A',
            checks: [
                { field: 'isActive', condition: 'VALUE_EQUALS', arrayValue: ['S'], }
            ]
        },
        {
            name: "status", value: status, setter: setStatus, checkboxSetter: checkboxStatusSetter, defaultValue: 'S',
            isVisible: statusRules !== 'A',
            checks: [
                { field: 'status', condition: 'VALUE_EQUALS', arrayValue: ['A'] }
            ]
        },
    ];

    function checkboxStatusSetter(value) {
        setStatus(value == 'A' ? 'R' : 'A')
    }

    useEffect(() => {
        if (!clientSelected || !clientSelectedHandled) return;
        setClientSelectedHandled(false);

        const fetchData = async () => {
            setInserting(false);
            setUpdating(false);
            ResetVariables.resetVariables(attributeList);
            await getRulesGrid();
        };

        fetchData();
    }, [clientSelectedHandled]);

    useEffect(() => {
        const fetchData = async () => {
            await getRulesDetail(0);
            await getRulesGrid();
        };

        fetchData();
    }, []);

    useEffect(() => {
        setDetailRecord(selectedsTopics);

        if (onSelectedsChange) {
            onSelectedsChange(selectedsTopics);
        }
    }, [selectedsTopics]);

    async function setDetailRecord(selectedsTopics) {
        if (selectedsTopics && selectedsTopics.length === 1) {
            setInserting(false);
            setUpdating(false);
            await getRulesDetail(selectedsTopics[0]);
        } else {
            setUpdating(false);
            ResetVariables.resetVariables(attributeList);
        }
    }

    async function getRulesGrid() {
        const parameters = {
            clientId: clientSelected ?? 0,
            isActive: 'S',
            statusRules: statusRules
        };
        const topicsRecordsResponse = await ExecuteAxios.api('get', `/subject-rules/get-subject-rules-active-grid/`, { params: parameters });
        setTopicsRecords(topicsRecordsResponse);
        RemoveIdsNotFound.remove(topicsRecordsResponse, setSelectedsTopics, selectedsTopics);
    }

    async function getRulesDetail(selectedTopic) {
        const parameters = { id: selectedTopic, statusRules: statusRules };
        const topicsRecordsDetail = await ExecuteAxios.api('get', `/subject-rules/get-subject-rules-by-id-detail/`, { params: parameters });
        setTopicsRecordDetail(topicsRecordsDetail);
        if (topicsRecordsDetail) {
            ApplyAttributes.applyAttributes(topicsRecordsDetail, attributeList, inserting);
        }
    }

    async function putRules() {
        try {
            setLoadingPanel(true);
            const parameters = {
                id: selectedsTopics[0],
                descriptionSubject: descriptionSubject,
                clientId: clientSelected ?? 0,
                isActive: isActive,
                status: status
            };
            return await ExecuteAxios.api('put', `/subject-rules/`, parameters);
        } finally {
            setLoadingPanel(false);
        }
    }

    async function postRules() {
        try {
            setLoadingPanel(true);
            const parameters = {
                descriptionSubject: descriptionSubject,
                clientId: clientSelected ?? 0,
                isActive: isActive,
                status: status
            };
            return await ExecuteAxios.api('post', `/subject-rules/`, parameters);
        } finally {
            setLoadingPanel(false);
        }
    }

    const defaultStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%'
    };

    return (
        <Panel
            titlePage={titlePage}
            className={className}
            style={style || defaultStyle}
            setUpdating={setUpdating}
            setInserting={setInserting}
            inserting={inserting}
            updating={updating}
            enableEdit={true}
            canEdit={selectedsTopics && selectedsTopics.length === 1}
            putFunction={putRules}
            enableNewRecord={true}
            canNewRecord={true}
            postFunction={postRules}
            canViewCRUD={true}
            recordsSelected={[]}
            viewOnlyOneRecordCRUD={false}
            resetVariables={() => { ResetVariables.resetVariables(attributeList); }}
            dataListedAttributes={topicsRecords}
            attributeList={attributeList}
            onUpdateIdSelected={setSelectedsTopics}
            reloadFilter={async () => { await getRulesGrid(); }}
            cancelFunction={async () => { await setDetailRecord(selectedsTopics); }}
        >
            {!inserting && !updating ? (
                <LoadingIndicator loaded={topicsRecords && !loadingPanel}>
                    <PanelListDefault
                        onUpdateIdSelected={setSelectedsTopics}
                        idsSelected={selectedsTopics}
                        data={topicsRecords}
                        attributeList={attributeList}
                        selectFirstRecord={true}
                        inserting={inserting}
                        updating={updating}
                    />
                </LoadingIndicator>
            ) : (
                <LoadingIndicator loaded={topicsRecordDetail && !loadingPanel}>
                    <AutomaticallyListedAttributes
                        list={topicsRecordDetail}
                        attributeList={attributeList}
                        readOnlyAttributes={!inserting && !updating}
                    />
                </LoadingIndicator>
            )}
        </Panel>
    );
}

export default Topics;
